import React, { useState, useEffect } from 'react';
import { getSubscribers, adminUnsubscribe, exportSubscribersUrl } from '@/lib/api';
import { Users, Loader2, Download, UserX, Mail, Check, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export default function SubscribersPage() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmUnsub, setConfirmUnsub] = useState(null);

  const loadData = () => { getSubscribers().then(setSubs).catch(console.error).finally(() => setLoading(false)); };
  useEffect(() => { loadData(); }, []);

  const handleUnsub = async () => {
    if (!confirmUnsub) return;
    try { await adminUnsubscribe(confirmUnsub.subscriber_id); setConfirmUnsub(null); loadData(); }
    catch (err) { console.error(err); }
  };

  const filtered = subs.filter(s => !search || s.email?.toLowerCase().includes(search.toLowerCase()));
  const activeCount = subs.filter(s => s.status === 'active').length;
  const unsubCount = subs.filter(s => s.status === 'unsubscribed').length;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="subscribers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Subscribers</h1>
          <p className="text-sm text-[#5C5C55] mt-1">{activeCount} active · {unsubCount} unsubscribed · {subs.length} total</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search email..." className="pl-10 bg-white rounded-xl border-[#E5E2DC] h-10 w-56" data-testid="sub-search" />
          </div>
          <a href={exportSubscribersUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[11px] px-4 py-2.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors"
            data-testid="export-csv">
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />Export CSV
          </a>
        </div>
      </div>

      {/* Summary */}
      <div className="flex gap-3 mb-6">
        <div className="bg-[#E8EDE5] rounded-2xl px-5 py-3">
          <p className="text-xs font-semibold text-[#3A4035]">Active</p>
          <p className="text-lg font-heading font-medium text-[#3A4035]">{activeCount}</p>
        </div>
        <div className="bg-[#F2F0EB] rounded-2xl px-5 py-3">
          <p className="text-xs font-semibold text-[#5C5C55]">Unsubscribed</p>
          <p className="text-lg font-heading font-medium text-[#5C5C55]">{unsubCount}</p>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.map(sub => (
          <div key={sub.subscriber_id} className="bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-4 flex items-center gap-4" data-testid={`sub-${sub.subscriber_id}`}>
            <div className="w-9 h-9 rounded-full bg-[#F2F0EB] flex items-center justify-center shrink-0">
              <Mail className="w-4 h-4 text-[#5C5C55]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A1A1A] truncate">{sub.email}</p>
              <p className="text-[10px] text-[#8A8B85]">
                {sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''} · {sub.signup_page || '/'}
              </p>
            </div>
            <span className={`text-[10px] px-2.5 py-1 rounded-full font-semibold ${sub.status === 'active' ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-[#F2F0EB] text-[#8A8B85]'}`}>
              {sub.status}
            </span>
            {sub.status === 'active' && (
              <button onClick={() => setConfirmUnsub(sub)}
                className="text-[11px] px-3 py-1.5 rounded-full font-medium text-[#8A8B85] border border-[#E5E2DC] hover:bg-[#F2F0EB] transition-colors"
                data-testid={`unsub-${sub.subscriber_id}`}>
                <UserX className="w-3 h-3 inline mr-1" strokeWidth={1.5} />Unsubscribe
              </button>
            )}
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <Users className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#8A8B85]">{search ? 'No subscribers match your search' : 'No subscribers yet'}</p>
        </div>
      )}

      {/* Confirm unsubscribe */}
      {confirmUnsub && (
        <Dialog open onOpenChange={() => setConfirmUnsub(null)}>
          <DialogContent className="sm:max-w-sm bg-white rounded-2xl border-[#E5E2DC]" data-testid="confirm-unsub-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Unsubscribe</DialogTitle>
              <DialogDescription className="text-xs text-[#5C5C55]">Remove {confirmUnsub.email} from the mailing list?</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-[#5C5C55]">They won't receive future emails and will be excluded from CSV exports.</p>
            <div className="flex gap-3 pt-1">
              <Button variant="outline" onClick={() => setConfirmUnsub(null)} className="flex-1 rounded-full h-10 border-[#E5E2DC]">Cancel</Button>
              <Button onClick={handleUnsub} className="flex-1 bg-[#B33A3A] text-white rounded-full h-10 hover:bg-[#8A2D2D]" data-testid="confirm-unsub-btn">Unsubscribe</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
